import { Phone, Mail, Facebook, Instagram, MapPin, Clock, ArrowRight } from "lucide-react";
import PageHeader from "@/components/PageHeader";
import FacebookBadge from "@/components/FacebookBadge";
import { site } from "@/data/site";

export default function Kontakt() {
    return (
        <div data-testid="page-kontakt">
            <PageHeader
                overline="Kontakt"
                title="Lad os tage en snak."
                subtitle="Skriv eller ring — så tager vi en snak og finder en løsning, der passer dig. Jeg kommer gerne ud og giver et helt uforpligtende tilbud. Ledige tider lige nu 👌"
            />

            <section className="py-16 sm:py-24 bg-[var(--ns-bg)]">
                <div className="container-ns">
                    <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-5 sm:gap-6 mb-14">
                        <a
                            href={site.phoneHref}
                            data-testid="kontakt-card-phone"
                            className="group card-ns p-8 sm:p-10 flex flex-col h-full"
                        >
                            <div className="w-14 h-14 rounded-md bg-[var(--ns-blue)] text-white grid place-items-center mb-6 group-hover:scale-105 transition-transform">
                                <Phone className="w-6 h-6" />
                            </div>
                            <div className="overline mb-2">
                                Ring direkte op
                            </div>
                            <div className="font-display font-black text-3xl sm:text-4xl tracking-tighter text-[var(--ns-text)] mb-3">
                                {site.phone}
                            </div>
                            <p className="text-[var(--ns-muted)] text-sm leading-relaxed flex-1">
                                Hverdage bedst mellem kl. 07–17. Udenfor arbejds­tid
                                er SMS også fint — så ringer jeg tilbage.
                            </p>
                            <span className="mt-5 inline-flex items-center gap-2 font-display font-bold text-sm text-[var(--ns-blue)] group-hover:gap-3 transition-all">
                                Ring nu <ArrowRight className="w-4 h-4" />
                            </span>
                        </a>

                        <a
                            href={site.emailHref}
                            data-testid="kontakt-card-email"
                            className="group card-ns p-8 sm:p-10 flex flex-col h-full"
                        >
                            <div className="w-14 h-14 rounded-md bg-[var(--ns-text)] text-white grid place-items-center mb-6 group-hover:scale-105 transition-transform">
                                <Mail className="w-6 h-6" />
                            </div>
                            <div className="overline mb-2">Skriv en mail</div>
                            <div className="font-display font-black text-xl sm:text-2xl tracking-tight text-[var(--ns-text)] mb-3 break-all">
                                {site.email}
                            </div>
                            <p className="text-[var(--ns-muted)] text-sm leading-relaxed flex-1">
                                Beskriv kort opgaven — gerne med nogle billeder
                                eller mål, hvis I har dem.
                            </p>
                            <span className="mt-5 inline-flex items-center gap-2 font-display font-bold text-sm text-[var(--ns-blue)] group-hover:gap-3 transition-all">
                                Åbn mail <ArrowRight className="w-4 h-4" />
                            </span>
                        </a>

                        <a
                            href={site.facebookUrl}
                            target="_blank"
                            rel="noopener noreferrer"
                            data-testid="kontakt-card-facebook"
                            className="group card-ns p-8 sm:p-10 flex flex-col h-full"
                        >
                            <div className="w-14 h-14 rounded-md bg-[var(--ns-blue-soft)] text-[var(--ns-blue)] grid place-items-center mb-6 group-hover:scale-105 transition-transform">
                                <Facebook className="w-6 h-6" />
                            </div>
                            <div className="overline mb-2">
                                Besked på Facebook
                            </div>
                            <div className="font-display font-black text-xl sm:text-2xl tracking-tight text-[var(--ns-text)] mb-3">
                                NS Tømrer på Facebook
                            </div>
                            <p className="text-[var(--ns-muted)] text-sm leading-relaxed flex-1">
                                Send en Messenger-besked — og se samtidig de
                                seneste projekter jeg deler på siden.
                            </p>
                            <span className="mt-5 inline-flex items-center gap-2 font-display font-bold text-sm text-[var(--ns-blue)] group-hover:gap-3 transition-all">
                                Åbn Facebook <ArrowRight className="w-4 h-4" />
                            </span>
                        </a>

                        <a
                            href={site.instagramUrl}
                            target="_blank"
                            rel="noopener noreferrer"
                            data-testid="kontakt-card-instagram"
                            className="group card-ns p-8 sm:p-10 flex flex-col h-full"
                        >
                            <div className="w-14 h-14 rounded-md bg-[var(--ns-blue-soft)] text-[var(--ns-blue)] grid place-items-center mb-6 group-hover:scale-105 transition-transform">
                                <Instagram className="w-6 h-6" />
                            </div>
                            <div className="overline mb-2">
                                Følg på Instagram
                            </div>
                            <div className="font-display font-black text-xl sm:text-2xl tracking-tight text-[var(--ns-text)] mb-3 break-all">
                                @ns_tomrer_ejendomsservive
                            </div>
                            <p className="text-[var(--ns-muted)] text-sm leading-relaxed flex-1">
                                Se billeder og videoer fra de seneste
                                opgaver — terrasser, renoveringer og
                                byggepladser.
                            </p>
                            <span className="mt-5 inline-flex items-center gap-2 font-display font-bold text-sm text-[var(--ns-blue)] group-hover:gap-3 transition-all">
                                Åbn Instagram <ArrowRight className="w-4 h-4" />
                            </span>
                        </a>
                    </div>

                    <div className="grid grid-cols-1 lg:grid-cols-2 gap-5 sm:gap-6">
                        <div className="card-ns p-8 sm:p-10">
                            <div className="flex items-center gap-4 mb-4">
                                <span className="w-11 h-11 rounded-md bg-[var(--ns-blue-soft)] text-[var(--ns-blue)] grid place-items-center">
                                    <MapPin className="w-5 h-5" />
                                </span>
                                <div className="overline">Dækningsområde</div>
                            </div>
                            <h3 className="font-display font-extrabold text-2xl tracking-tight mb-3">
                                Silkeborg og omegn
                            </h3>
                            <p className="text-[var(--ns-muted)] leading-relaxed">
                                Jeg arbejder dagligt i Silkeborg og de
                                omkringliggende byer. Bor I lidt længere væk?
                                Ring endelig — så tager vi en snak alligevel.
                            </p>
                        </div>

                        <div className="card-ns p-8 sm:p-10">
                            <div className="flex items-center gap-4 mb-4">
                                <span className="w-11 h-11 rounded-md bg-[var(--ns-blue-soft)] text-[var(--ns-blue)] grid place-items-center">
                                    <Clock className="w-5 h-5" />
                                </span>
                                <div className="overline">Træffetid</div>
                            </div>
                            <h3 className="font-display font-extrabold text-2xl tracking-tight mb-3">
                                Hverdage 07 – 17
                            </h3>
                            <p className="text-[var(--ns-muted)] leading-relaxed">
                                Er I ude på pladsen? Send en SMS eller en
                                besked på Facebook — så vender jeg tilbage så
                                snart jeg har hænderne fri.
                            </p>
                        </div>
                    </div>

                    <div className="mt-14 flex flex-col sm:flex-row sm:items-center justify-between gap-5 p-6 sm:p-8 rounded-md bg-white border border-[var(--ns-border)]">
                        <div>
                            <div className="font-display font-extrabold text-lg mb-1">
                                Foretrækker I at høre fra tidligere kunder?
                            </div>
                            <p className="text-[var(--ns-muted)] text-sm">
                                Læs 5 rigtige anmeldelser på Facebook — alle
                                100 % positive.
                            </p>
                        </div>
                        <FacebookBadge />
                    </div>
                </div>
            </section>
        </div>
    );
}
